import { useState } from 'react';
import { CircleAlert, GitBranch, Settings2, Sparkles, Video } from 'lucide-react';
import { DemoRequestDialog } from '@/components/landing/DemoRequestDialog';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { TimelineVisual } from '@/components/home/StorySections';
import { CheckList, CtaPanel, DataTable, HeroActions, IconCardGrid, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading } from '@/components/page/PageKit';
import { AutomationFlowVisual } from '@/components/page/PageVisuals';
import { AGENCY_LINK, FEATURES_LINK, PRODUCT_LINKS } from '@/components/page/relatedLinks';
import { useSEO } from '@/hooks/useSEO';

const rows = [
  ['Application submitted', 'Parse PDF or DOCX resume; calculate job-match score; check duplicate email'],
  ['Screening rules satisfied', 'Move the candidate to the configured stage'],
  ['Interview stage reached', 'Send AI interview, Work Style assessment, or both'],
  ['Candidate completes interview', 'Generate per-dimension scores, radar chart, overall score, and ranking'],
  ['Face-to-Face stage reached', 'Create the Prepare Interview report for recruiter review'],
  ['Scheduling status changes', 'Keep the supported Zoom or Calendly lifecycle in sync'],
];

const branches = [
  { icon: Video, title: 'AI interview', body: 'Timed video questions followed by scores and ranking after completion.' },
  { icon: Sparkles, title: 'Work Style', body: 'Six product dimensions, quality flags, and a shareable report.' },
  { icon: GitBranch, title: 'Both', body: 'Send both configured invitations when the candidate reaches the stage.' },
];

export default function RecruitmentAutomation() {
  const [demo, setDemo] = useState(false);
  useSEO({ title: 'Recruitment Automation Software for Agencies | Beatview', description: 'Automate configured steps between application and interview with screening rules, invitations, completed-interview ranking and a visible event log.', canonicalUrl: 'https://beatview.ai/recruitment-automation' });

  return (
    <PageShell>
      <PageStructuredData name="Recruitment Automation Software for Agencies" description="Automate configured steps between application and interview." url="https://beatview.ai/recruitment-automation" />
      <PageHero eyebrow="Recruitment automation software" title={<>Automate the steps <span className="bv-hero-highlight">between application and interview.</span></>} description="Configure the screening rules and stage actions once. Beatview performs the connected steps, while the candidate event log shows your team what happened." visual={<AutomationFlowVisual />}>
        <HeroActions onDemo={() => setDemo(true)} />
      </PageHero>

      <Section tone="white" className="py-14 lg:py-16" narrow>
        <InShort>Recruitment automation in Beatview means your team configures screening rules and stage actions once. The agent then performs the connected steps — scoring, moving, inviting and ranking — and records each one in the candidate event log for recruiter review.</InShort>
      </Section>

      <Section tone="wash" labelledBy="triggers-title">
        <SectionHeading id="triggers-title" eyebrow="Trigger and action map" title="What starts each automated step." description="Availability depends on your plan and the settings your team enables." />
        <Reveal delay={0.1} className="mt-10">
          <DataTable caption="Beatview automation triggers and actions" columns={['Trigger', 'Configured action', 'Visible result']} rows={rows.map(([trigger, action], i) => [trigger, action, `Candidate event ${i < 4 ? 'recorded' : 'updated'}`])} />
        </Reveal>
      </Section>

      <Section tone="white" labelledBy="branches-title">
        <SectionHeading id="branches-title" eyebrow="Invitation branches" title="Choose what happens at the interview stage." />
        <IconCardGrid className="mt-12" items={branches} />
      </Section>

      <Section tone="dark" labelledBy="event-log-title">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading tone="dark" id="event-log-title" eyebrow="Candidate event log" title="See the action, result, and next stage." />
            <Reveal delay={0.1}><p className="mt-6 text-lg leading-8 text-white/70">The event log records candidate activity for recruiter review. It is operational history — not a legal certification or a claim of immutable model auditing.</p></Reveal>
          </div>
          <Reveal delay={0.1}><div className="rounded-3xl bg-white p-6 shadow-bv-deep sm:p-8"><TimelineVisual /></div></Reveal>
        </div>
      </Section>

      <Section tone="base">
        <div className="grid gap-5 md:grid-cols-2">
          <Reveal>
            <article className="h-full rounded-3xl border border-bv-blue-line bg-white p-7 sm:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue"><Settings2 className="h-5 w-5" /></span>
              <h2 className="mt-5 font-display text-2xl font-semibold text-bv-ink">Your team configures</h2>
              <CheckList className="mt-5" items={['Score thresholds, screening answers, and location filters', 'Which invitation is sent at each stage', 'Questions, timing, job settings, and supported connections']} />
            </article>
          </Reveal>
          <Reveal delay={0.08}>
            <article className="h-full rounded-3xl border border-bv-blue-line bg-white p-7 sm:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bv-orange-soft text-bv-coral"><CircleAlert className="h-5 w-5" /></span>
              <h2 className="mt-5 font-display text-2xl font-semibold text-bv-ink">Paths that still need attention</h2>
              <CheckList className="mt-5" icon="minus" items={['A candidate may not open or complete an invitation', 'A failed upload or incomplete answer needs review', 'Recruiters still decide what to recommend to a client']} />
            </article>
          </Reveal>
        </div>
      </Section>

      <RelatedLinks title="Where automation fits in the agent" items={[AGENCY_LINK, PRODUCT_LINKS[0], PRODUCT_LINKS[1], FEATURES_LINK]} />

      <CtaPanel eyebrow="Map your workflow" title={<>Map your <span className="bv-hero-highlight">application-to-interview</span> workflow.</>} description="Start with one active role for free, or walk the team through your stages and rules." onDemo={() => setDemo(true)} />
      <DemoRequestDialog open={demo} onOpenChange={setDemo} />
    </PageShell>
  );
}
